/**
 * Converts the SubscriptionScheduler state object into a cron expression string.
 *
 * @param {object} opts
 * @param {string} opts.repeat        - 'after_refresh' | 'hourly' | 'daily' | 'weekly' | 'monthly'
 * @param {string} opts.hour          - '00'–'23'
 * @param {string} opts.minute        - '00' | '05' | … | '55'
 * @param {string[]} opts.weekdays    - subset of ['MON','TUE','WED','THU','FRI','SAT','SUN']
 * @param {string} opts.monthDayType  - 'specific' | 'last'
 * @param {number} opts.monthDay      - 1–31
 * @returns {string} cron expression
 */
export function schedulerToCron({ repeat, hour = '08', minute = '00', weekdays = ['MON'], monthDayType = 'specific', monthDay = 1 } = {}) {
  const h = parseInt(hour,   10);
  const m = parseInt(minute, 10);

  switch (repeat) {
    case 'after_refresh':
      // Runs once daily at a fixed early hour — actual trigger is external
      return `0 6 * * *`;

    case 'hourly':
      return `${m} * * * *`;

    case 'daily':
      return `${m} ${h} * * *`;

    case 'weekly': {
      const dayMap = { MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6, SUN: 0 };
      const days   = (weekdays.length ? weekdays : ['MON'])
        .map(d => dayMap[d])
        .sort((a, b) => a - b)
        .join(',');
      return `${m} ${h} * * ${days}`;
    }

    case 'monthly':
      if (monthDayType === 'last') {
        // node-cron supports 'L' for last day; if your scheduler doesn't, use 28 as safe fallback
        return `${m} ${h} L * *`;
      }
      return `${m} ${h} ${Math.max(1, Math.min(31, monthDay))} * *`;

    default:
      return `${m} ${h} * * *`;
  }
}

/**
 * Attempts to parse a simple cron expression back into a scheduler state object.
 * Only handles the patterns that schedulerToCron produces — not arbitrary cron strings.
 *
 * @param {string} cron
 * @returns {object} partial scheduler state (best-effort)
 */
export function cronToScheduler(cron) {
  if (!cron) return {};
  const parts = cron.trim().split(/\s+/);
  if (parts.length < 5) return {};

  const [min, hr, dom, , dow] = parts;
  const hour   = hr  === '*' ? '08' : String(parseInt(hr,  10)).padStart(2, '0');
  const minute = min === '*' ? '00' : String(parseInt(min, 10)).padStart(2, '0');

  // hourly
  if (hr === '*') return { repeat: 'hourly', hour, minute };

  // monthly — last day
  if (dom === 'L') return { repeat: 'monthly', monthDayType: 'last', hour, minute };

  // monthly — specific day
  if (dom !== '*' && dow === '*') {
    return { repeat: 'monthly', monthDayType: 'specific', monthDay: parseInt(dom, 10), hour, minute };
  }

  // weekly
  if (dom === '*' && dow !== '*') {
    const revMap = { 0: 'SUN', 1: 'MON', 2: 'TUE', 3: 'WED', 4: 'THU', 5: 'FRI', 6: 'SAT' };
    const weekdays = dow.split(',').map(d => revMap[parseInt(d, 10)]).filter(Boolean);
    return { repeat: 'weekly', weekdays, hour, minute };
  }

  // daily
  return { repeat: 'daily', hour, minute };
}